// compareSelection.ts — pure state helpers for Vergleich's compact "welche Szenarien
// einbeziehen" include/exclude control: WHICH scenarios (of the full N-scenario list,
// lib/storage.ts) are currently included as table columns / chart series. Everything is
// included by default (auto-compute means every scenario always HAS a result — see
// app.tsx — so there is no separate "not yet computed" gate to filter on anymore); this
// is purely a decluttering aid for someone with many scenarios who wants to focus the
// comparison table/chart on a few of them. Kept separate from VergleichView.tsx so the
// selection logic is unit-testable without mounting a component.
//
// The mobile layout no longer needs a "which ONE column is active" concept — the
// Kennzahlen table scrolls horizontally with a sticky label column instead of switching
// between single columns (see global.css's .compare-table-wrap), so this file dropped its
// former resolveMobileActiveId helper along with that layout.
export type CompareSelection = string[];

/** Every scenario selected, in list order — the initial/reset selection (all columns
 * shown, desktop side-by-side / mobile behind the tab switcher). */
export function defaultCompareSelection(scenarioIds: string[]): CompareSelection {
  return [...scenarioIds];
}

/** Adds/removes `id` from the selection, preserving the OTHER ids' relative order and
 * appending a newly-added id at the end. Refuses to drop the last remaining id — Vergleich
 * always shows at least one column, per the feature spec ("how many, down to 1"). */
export function toggleCompareSelection(selected: CompareSelection, id: string): CompareSelection {
  if (selected.includes(id)) {
    if (selected.length <= 1) return selected;
    return selected.filter((x) => x !== id);
  }
  return [...selected, id];
}

/** Prunes a selection down to ids that still exist (a scenario may have been deleted, or
 * this is the very first render before any explicit choice was made), preserving order.
 * Falls back to every existing id when that would otherwise empty the selection, and to
 * [] only when there are no scenarios at all — never throws, never shows a stale/deleted
 * scenario's column. */
export function sanitizeCompareSelection(selected: CompareSelection, existingIds: string[]): CompareSelection {
  const pruned = selected.filter((id) => existingIds.includes(id));
  if (pruned.length > 0) return pruned;
  return [...existingIds];
}
